import { AsyncPipe } from '@angular/common';
import { Component } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { combineLatest, map, Observable, startWith } from 'rxjs';
import { IEmployee } from 'src/app/employee/employee';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { ISearchTimeEntriesRequest, ITimeEntry } from 'src/app/time-tracking/models/time-entry';
import { ITimeEntryCategory } from 'src/app/time-tracking/models/time-entry-category';
import { IAssignment } from '../../models/assignment';
import { AssignmentStorageService } from '../../storage/assignment-storage.service';

interface ITimeEntriesViewModel {
  employees: IEmployee[];
  categories: ITimeEntryCategory[];
  timeEntries: ITimeEntry[];
  totalTime: string;
}

@Component({
    selector: 'app-assignment-time-entries',
    templateUrl: './assignment-time-entries.component.html',
    styleUrls: ['./assignment-time-entries.component.css'],
    standalone: false
})
export class AssignmentTimeEntriesComponent {

  constructor(
    private assignmentStore: AssignmentStorageService,
    private asyncPipe: AsyncPipe,
    private fb: UntypedFormBuilder,
  ) { }

  public assignment$: Observable<IAssignment | null> =
    this.assignmentStore.assignment$;

  searchRequest: ISearchTimeEntriesRequest = {
    pageNumber: 0,
    pageSize: 25,
    orderBy: ['Date desc', 'Start']
  }

  filterForm = this.fb.group({
    employee: [null],
    timeEntryCategory: [null],
    dateRangeStart: [null],
    dateRangeEnd: [null],
  });

  // Empty selection markers for the dropdowns so the user can clear a filter
  // back to "show all".
  employeeDefaultItem = { id: null } as IEmployee;
  categoryDefaultItem = { id: null } as ITimeEntryCategory;

  // The assignment already carries all of its time entries, so filtering by
  // employee/activity and recomputing the total happens client-side. This lets
  // the customer service team narrow the list for billing without needing
  // access to the admin time-tracking view.
  vm$: Observable<ITimeEntriesViewModel> = combineLatest([
    this.assignment$,
    this.filterForm.valueChanges.pipe(startWith(this.filterForm.value)),
  ]).pipe(
    map(([assignment, filter]) => {
      const allEntries = assignment?.timeEntries ?? [];

      // Date bounds are compared locally (entry.date is already a Date), with the
      // "to" day inclusive.
      const from = filter.dateRangeStart ? this.startOfDay(filter.dateRangeStart) : null;
      const to = filter.dateRangeEnd ? this.endOfDay(filter.dateRangeEnd) : null;

      const filteredEntries = allEntries.filter(entry =>
        (!filter.employee?.id || entry.employeeId === filter.employee.id) &&
        (!filter.timeEntryCategory?.id || entry.timeEntryCategoryId === filter.timeEntryCategory.id) &&
        (!from || entry.date >= from) &&
        (!to || entry.date <= to)
      );

      return {
        employees: this.getDistinctEmployees(allEntries),
        categories: this.getDistinctCategories(allEntries),
        timeEntries: filteredEntries,
        totalTime: this.calculateTotalTime(filteredEntries),
      };
    })
  );

  private getDistinctEmployees(entries: ITimeEntry[]): IEmployee[] {
    const employees = new Map<string, IEmployee>();
    entries.forEach(entry => {
      if (entry.employee && !employees.has(entry.employeeId)) {
        employees.set(entry.employeeId, entry.employee);
      }
    });
    return Array.from(employees.values())
      .sort((a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? ''));
  }

  private getDistinctCategories(entries: ITimeEntry[]): ITimeEntryCategory[] {
    const categories = new Map<string, ITimeEntryCategory>();
    entries.forEach(entry => {
      if (entry.timeEntryCategory && !categories.has(entry.timeEntryCategoryId)) {
        categories.set(entry.timeEntryCategoryId, entry.timeEntryCategory);
      }
    });
    return Array.from(categories.values())
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  private calculateTotalTime(entries: ITimeEntry[]): string {
    let total = new TimeSpan();
    entries.forEach(entry => {
      if (entry.duration) {
        total = total.add(TimeSpan.parse(entry.duration));
      }
    });
    return total.toString();
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private endOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  }

  onResetFilter() {
    this.filterForm.reset({ employee: null, timeEntryCategory: null, dateRangeStart: null, dateRangeEnd: null });
  }

  updateList() {
    this.assignmentStore.getAssignment(this.asyncPipe.transform(this.assignment$).id);
  }

}
